import { type MaybeRefOrGetter, type Ref, toValue } from 'vue'
import { upperCaseFirst } from 'change-case-all'
import type { RouteLocationGeneric } from 'vue-router'

export function assertNever<T>(_Y_: never, ret: T): T {
    return ret
}

export function includes<
    T extends string | number | bigint | boolean | null | undefined,
>(array: T[] | Readonly<T[]>, value: unknown): value is T {
    return array.includes(value as any)
}

export function filterSupportedValues<
    T extends string | number | bigint | boolean | null | undefined,
>(
    supportedValues: T[],
    actualValues: (string | number | bigint | boolean | null | undefined)[]
): `${T}`[] {
    return actualValues.filter((v) =>
        supportedValues.includes(v as any)
    ) as `${T}`[]
}

export function arrayUnique<T extends string | number>(array: T[]): T[] {
    return array.filter((item, index) => array.indexOf(item) === index)
}

export function getNumber(number: unknown): number | undefined {
    if (typeof number === 'number') {
        if (Number.isNaN(number)) {
            return undefined
        }

        return number
    }

    if (typeof number === 'string') {
        if (!number.trim()) {
            return undefined
        }

        const value = Number(number.trim())

        if (Number.isNaN(value)) {
            return undefined
        }

        return value
    }

    return undefined
}

export function isNotUndefinedOrNull<T>(v: T | undefined | null): v is T {
    return !isUndefinedOrNull(v)
}

export function isUndefinedOrNull<T>(
    v: T | undefined | null
): v is undefined | null {
    return v === undefined || v === null
}

export function areUndefinedOrNull<T>(v: (T | undefined | null)[]): v is T[] {
    return v.every(isUndefinedOrNull)
}

export type RefType<T> = T extends Ref<infer B> ? B : never
export type GraphQLTypeName<T> = T extends { __typename: infer Z } ? Z : never

export function keys<Key extends keyof any>(obj: Record<Key, any>): Key[] {
    return Object.keys(obj) as Key[]
}

export function percent(amount: number, percent: number): number {
    return (amount * percent) / 100
}

export function systemCodeToWords(code: string): string {
    return code
        .split('_')
        .map((p) => {
            return upperCaseFirst(p.toLowerCase())
        })
        .join(' ')
}

export function clamp(x: number, min: number, max: number): number {
    if (x < min) {
        return min
    }

    if (x > max) {
        return max
    }

    return x
}

export type RefOrGetter<T = any> = Ref<T> | (() => T)

export type ReadonlyRef<T> = Readonly<Ref<T>>

export type ExcludeNull<T> = Exclude<T, null>
export type ExcludeUndefined<T> = Exclude<T, undefined>
export type ExcludeNullUndefined<T> = ExcludeNull<ExcludeUndefined<T>>

type RouteNameAndMeta = Pick<RouteLocationGeneric, 'name' | 'meta'>

export function isSameRoute(
    from: RouteNameAndMeta,
    to: RouteNameAndMeta
): boolean {
    if (from.meta.name && to.meta.name && from.meta.name === to.meta.name) {
        // We have WR / ER that have two routes on the same component
        // The route names are different, however, for the sake of
        // Suspense working correctly, we set route.meta.name
        // If it's the same, the user has not navigated away
        return true
    }

    return to.name === from.name
}

// https://stackoverflow.com/questions/60141960/typescript-key-value-relation-preserving-object-entries-type
export type Entries<T> = {
    [K in keyof T]: [K, T[K]]
}[keyof T][]

// https://github.com/microsoft/TypeScript/issues/31751#issuecomment-498526919
export type IsNever<T> = [T] extends [never] ? true : false

export type ArgumentsType<T> = T extends (...args: infer U) => any ? U : never

export type CallBuffer<T> = {
    [K in keyof T]: [K, ArgumentsType<T[K]>]
}[keyof T][]

export function debug<T>(val: MaybeRefOrGetter<T>): T {
    return JSON.parse(JSON.stringify(toValue(val)))
}

export function logUnexpectedError(
    error: string,
    args: Record<string, any>
): void {
    // eslint-disable-next-line no-console
    console.error(`Unexpected Error: ${error}`, args)
}
